/* eslint-disable @typescript-eslint/no-unused-vars */
import { Directive, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';
import { Nullable } from '@mosaik/core';
import { DisplayDensity, ElementExtensions, PropertyChangedEventArgs, Size } from '@mosaik/presentation';
import { AnyExtensions } from '../../Extensions/AnyExtensions';
import { IVisualProps, Visual } from './Visual';

/**
 * A property interface of all properties that contained in the {@link UIElement}.
 * Extends the {@link IVisualProps} interface.
 * @public
 */
export interface IUIElementProps<TElement extends HTMLElement = HTMLElement> extends IVisualProps {
  id: string;
  focusable: boolean;
  isFocused: boolean;
  isVisible: boolean;
  isEnabled: boolean;
  disabled: boolean;
  tabIndex: Nullable<number>;
  density: DisplayDensity;
  readonly nativeElement: TElement;
  readonly densityChanged: EventEmitter<PropertyChangedEventArgs<DisplayDensity>>;
  readonly focused: EventEmitter<undefined>;
  readonly unfocused: EventEmitter<undefined>;
}

/**
 * todo doc
 * Extends the {@link Visual} class.
 * Implements the {@link IUIElementProps} interface.
 * @public
 * @dynamic
 */
@Directive()
export abstract class UIElement<TElement extends HTMLElement = HTMLElement> extends Visual implements IUIElementProps<TElement> {
  private readonly _nativeElement: TElement;
  private readonly _offsetSize: Size;
  private readonly _densityChanged: EventEmitter<PropertyChangedEventArgs<DisplayDensity>>;
  private readonly _focused: EventEmitter<undefined>;
  private readonly _unfocused: EventEmitter<undefined>;
  private _id: string;
  private _focusable: boolean;
  private _isFocused: boolean;
  private _isVisible: boolean;
  private _isEnabled: boolean;
  private _disabled: boolean;
  private _tabIndex: Nullable<number>;
  private _density: DisplayDensity;

  public constructor(nativeElement: TElement) {
    super();
    this._nativeElement = nativeElement;
    this._id = ''; // #todo: set this value initial
    this._focusable = this.focusable = false; // #todo: set this value initial from this._nativeElement.tabIndex;
    this._isFocused = this.isFocused = false; // #todo: set this value initial from document.activeElement === this._nativeElement;
    this._isVisible = this.isVisible = true; // #todo: set this value initial
    this._isEnabled = this.isEnabled = true; // #todo: set this value initial
    this._disabled = this.disabled = false; // #todo: set this value initial
    this._tabIndex = this.tabIndex = null;
    this._offsetSize = new Size(this._nativeElement.offsetWidth, this._nativeElement.offsetHeight);
    this._density = this.density = DisplayDensity.Comfortable;
    this._densityChanged = new EventEmitter<PropertyChangedEventArgs<DisplayDensity>>();
    this._focused = new EventEmitter<undefined>();
    this._unfocused = new EventEmitter<undefined>();
  }

  /**
   * Gets or sets the id of the {@link UIElement}.
   * Sets the `id` dom attribute as value, otherwise nothing.
   * @public
   * @type string
   * @default ''
   */
  @Input()
  @HostBinding('attr.id')
  public get id(): string {
    return this._id;
  }
  public set id(value: string) {
    this._id = value;
  }

  /**
   * Gets or sets a value that indicates whether the {@link UIElement} can receive focus.
   * Sets the `focusable` dom class property as true value, otherwise `unfocusable`.
   * @public
   * @type boolean
   * @default false
   */
  @Input()
  public get focusable(): boolean {
    return this._focusable;
  }
  public set focusable(value: boolean) {
    const focusable = AnyExtensions.coerceBoolean(value);
    if (this._focusable !== focusable) {
      this._focusable = focusable;
      this.isFocused = focusable ? this._isFocused : false;
      this.tabIndex = focusable ? 1 : null;
      ElementExtensions.toggleClass(this._nativeElement, 'focusable', 'unfocusable', focusable);
      this.onFocusableChanged();
    }
  }

  /**
   * Gets or sets a value that determines whether the {@link UIElement} has logical focus.
   * Sets the `focused` dom class property as true value, otherwise `unfocused`.
   * @public
   * @type boolean
   * @default false
   */
  @Input()
  public get isFocused(): boolean {
    return this._isFocused;
  }
  public set isFocused(value: boolean) {
    if (this.focusable) {
      const isFocused = AnyExtensions.coerceBoolean(value);
      if (this._isFocused !== isFocused) {
        ElementExtensions.toggleClass(this._nativeElement, 'focused', 'unfocused', isFocused);
        this.onIsFocusedChanged(this._isFocused, (this._isFocused = isFocused));
      }
    }
  }

  /**
   * Occurs when the value of the `isVisible` property changes on the {@link UIElement}.
   * Sets the `visible` dom class property as true value, otherwise `invisible`.
   * @public
   * @type boolean
   * @default true
   */
  @Input()
  public get isVisible(): boolean {
    return this._isVisible;
  }
  public set isVisible(value: boolean) {
    const isVisible = AnyExtensions.coerceBoolean(value);
    if (this._isVisible !== isVisible) {
      this._isVisible = isVisible;
      ElementExtensions.toggleClass(this._nativeElement, 'visible', 'invisible', isVisible);
      this.onIsVisibleChanged();
    }
  }

  /**
   * Gets or sets a value indicating whether the {@link UIElement} is enabled in the user interface(UI).
   * Sets the `enabled` dom class property as true value, otherwise `disabled`.
   * @deprecated use `disabled` property instead.
   * @public
   * @type boolean
   * @default true
   */
  @Input()
  public get isEnabled(): boolean {
    return this._isEnabled;
  }
  public set isEnabled(value: boolean) {
    const isEnabled = AnyExtensions.coerceBoolean(value);
    if (this._isEnabled !== isEnabled) {
      this._isEnabled = isEnabled;
      ElementExtensions.toggleClass(this.nativeElement, 'enabled', 'disabled', isEnabled);
      ElementExtensions.toggleAttribute(
        this.nativeElement,
        { qualifiedName: 'aria-disabled', value: !isEnabled },
        { qualifiedName: 'aria-disabled', value: isEnabled },
        isEnabled
      );
      // tslint:disable-next-line: deprecation
      this.onIsEnabledChanged();
      // TODO: remove when the `isEnabled` property is gone.
      this.disabled = !isEnabled;
    }
  }

  /**
   * Gets or sets a value indicating whether the {@link UIElement} is disabled in the user interface(UI).
   * Sets the `disabled` dom class property as true value, otherwise `enabled`.
   * @public
   * @type boolean
   * @default true
   */
  @Input()
  public get disabled(): boolean {
    return this._disabled;
  }
  public set disabled(value: boolean) {
    const disabled = AnyExtensions.coerceBoolean(value);
    if (this._disabled !== disabled) {
      this._disabled = disabled;
      ElementExtensions.toggleClass(this.nativeElement, 'disabled', 'enabled', disabled);
      ElementExtensions.toggleAttribute(
        this.nativeElement,
        { qualifiedName: 'aria-disabled', value: disabled },
        { qualifiedName: 'aria-disabled', value: !disabled },
        disabled
      );
      this.onDisabledChanged();
      // TODO: remove when the `isEnabled` property is gone.
      // tslint:disable-next-line: deprecation
      this.isEnabled = !disabled;
    }
  }

  /**
   * Gets or sets a value indicating whether the {@link UIElement} is a tab stop or not.
   * Sets the `tab-index` dom attribute as value, otherwise nothing.
   * @public
   * @type Nullable<number>
   * @default number
   */
  @Input()
  @HostBinding('attr.tabIndex')
  public get tabIndex(): Nullable<number> {
    return this._tabIndex;
  }
  public set tabIndex(value: Nullable<number>) {
    const tabIndex = value === null ? value : AnyExtensions.coerceNumber(value);
    if (this._tabIndex !== tabIndex) {
      this._tabIndex = value;
      ElementExtensions.toggleAttribute(
        this._nativeElement,
        { qualifiedName: 'tabIndex', value: 1 },
        { qualifiedName: 'tabIndex', value: 0 },
        tabIndex !== 0
      );
    }
  }

  /**
   * Gets or sets the `DisplayDensity` of the {@link UIElement}.
   * Posible values are: `comfortable` `cosy` `compact`
   * @public
   * @type DisplayDensity
   * @default DisplayDensity.Comfortable
   */
  @Input()
  public get density(): DisplayDensity {
    return this._density;
  }
  public set density(value: DisplayDensity) {
    let density = value;
    switch (value) {
      case DisplayDensity.Comfortable:
      case DisplayDensity.Compact:
      case DisplayDensity.Cosy:
        density = value;
        break;
      default:
        density = DisplayDensity.Comfortable;
    }

    if (this._density !== density) {
      if (!this._density) {
        this._nativeElement.classList.add(density);
        this._density = density;
        return;
      }
      if (!this._nativeElement.classList.contains(this._density)) {
        this._nativeElement.classList.add(this._density);
      } else {
        this._nativeElement.classList.replace(this._density, density);
      }
      this.onDensityChanged(this._density, (this._density = density));
    }
  }

  /**
   * Gets the native element.
   * @public
   * @readonly
   * @type TElement
   */
  public get nativeElement(): TElement {
    return this._nativeElement;
  }

  /**
   * Called when `Density` property changed.
   * Provides reference to old and new `DisplayDensity` as event argument.
   * @public
   * @eventProperty
   * @readonly
   * @type EventEmitter<PropertyChangedEventArgs<DisplayDensity>>
   * @default EventEmitter
   */
  @Output()
  public get densityChanged(): EventEmitter<PropertyChangedEventArgs<DisplayDensity>> {
    return this._densityChanged;
  }

  /**
   * Called when the {@link UIElement} is focused.
   * @public
   * @readonly
   * @type EventEmitter<undefined>
   * @default EventEmitter
   */
  @Output()
  public get focused(): EventEmitter<undefined> {
    return this._focused;
  }

  /**
   * Called when the {@link UIElement} is unfocused.
   * @public
   * @readonly
   * @type EventEmitter<undefined>
   * @default EventEmitter
   */
  @Output()
  public get unfocused(): EventEmitter<undefined> {
    return this._unfocused;
  }

  /**
   * Returns the native element text content or a empty `string` if the text content is `null`.
   * @public
   * @returns string
   */
  public override toString(): string {
    return this._nativeElement.textContent || '';
  }

  /**
   * Returns the size of the native element (`offsetWidth` and `offsetHeight`).
   * @public
   * @returns Size
   */
  public offsetSize(): Size {
    return this._offsetSize.reset(this._nativeElement.offsetWidth, this._nativeElement.offsetHeight);
  }

  /**
   * @protected
   * @virtual
   * @param event - The focus event
   */
  @HostListener('focusin', ['$event'])
  public onFocusin(event: FocusEvent): void {
    if (this._focusable && !this._isFocused) {
      this.onIsFocusedChanged(this._isFocused, (this._isFocused = true));
    }
  }

  /**
   * @protected
   * @virtual
   * @param event - The focus event
   */
  @HostListener('focusout', ['$event'])
  public onFocusout(event: FocusEvent): void {
    if (this._focusable && this._isFocused) {
      this.onIsFocusedChanged(this._isFocused, (this._isFocused = false));
    }
  }

  /**
   * @protected
   * @virtual
   * @param event - The keyboard event
   */
  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    // override
  }

  /**
   * @protected
   * @virtual
   * @param event - The keyboard event
   */
  @HostListener('keyup', ['$event'])
  public onKeyUp(event: KeyboardEvent): void {
    // override
  }

  /**
   * @protected
   * @virtual
   * @param event - The mouse event
   */
  @HostListener('click', ['$event'])
  public onClick(event: MouseEvent): void {}

  /**
   * @deprecated Use `onDisabledChanged` method instead
   * @protected
   * @virtual
   */
  public onIsEnabledChanged(): void {}

  /**
   * @protected
   * @virtual
   */
  public onDisabledChanged(): void {}

  /**
   * @protected
   * @virtual
   */
  public onDensityChanged(oldValue: DisplayDensity, newValue: DisplayDensity): void {
    const args = new PropertyChangedEventArgs<DisplayDensity>(oldValue, newValue);
    if (this._densityChanged) {
      this._densityChanged.emit(args);
    }
  }

  /**
   * @protected
   * @virtual
   */
  public onIsVisibleChanged(): void {}

  /**
   * @protected
   * @virtual
   */
  public onIsFocusedChanged(oldValue: boolean, newValue: boolean): void {
    if (newValue) {
      this._focused.emit();
    } else {
      this._unfocused.emit();
    }
  }

  /**
   * @protected
   * @virtual
   */
  public onFocusableChanged(): void {}

  /**
   * @protected
   * @virtual
   */
  public onTabIndexChanged(): void {}
}
